import { useCallback, useEffect, useState } from 'react';
import type { CondimentSaleDTO, CondimentSaleStatus, CreateCondimentSaleRequest, Page } from '@six-pockets/shared';
import { condimentSalesApi } from '@/api';
import { PlusIcon, SearchIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, ConfirmModal, DatePicker, InlineAlert, Pagination, SegmentedControl, TextField, type SegmentOption } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { useTableActions, useTablesData } from '@/context/TablesContext';
// The one-page BillModal is parked (see BillModal.tsx); BillStepsModal takes the same props.
import { BillStepsModal } from '@/features/billing/components/BillStepsModal';
import { SaleFormModal } from '@/features/condiments/components/SaleFormModal';
import { SaleRow } from '@/features/condiments/components/SaleRow';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { todayKey } from '@/utils/date';
import styles from '@/styles/pages/CondimentsPage/style.scss';

const PAGE_SIZE = 12;

const STATUS_OPTIONS: SegmentOption<CondimentSaleStatus>[] = [
  { value: 'open', label: 'Open' },
  { value: 'billed', label: 'Billed' },
];

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/**
 * Condiment tabs, the only place condiments are recorded. Filter by Open / Billed and the day opened (today by
 * default), search by tab name, and page through the results; the server does the filtering, search and paging.
 */
export function CondimentsPage() {
  const { condiments } = useTablesData();
  const { refresh: refreshTables, refreshOpenSales } = useTableActions();
  const canBill = useAuth().can('bills');

  const [status, setStatus] = useState<CondimentSaleStatus>('open');
  const [date, setDate] = useState(todayKey);
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const [result, setResult] = useState<Page<CondimentSaleDTO> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<CondimentSaleDTO | 'new' | null>(null);
  const [deleting, setDeleting] = useState<CondimentSaleDTO | null>(null);
  const [billing, setBilling] = useState<CondimentSaleDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // A new search goes back to the first page.
  useEffect(() => setPage(1), [query]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    condimentSalesApi.list({ status, date, q: query || undefined, page, pageSize: PAGE_SIZE }, ctrl.signal).then(
      (r) => {
        // Deleting or billing the last tab on a page leaves it empty: step back a page.
        if (r.items.length === 0 && r.page > 1) setPage(r.page - 1);
        else setResult(r);
        setLoading(false);
        // Keep the sidebar badge in step with the list (tabs may have changed elsewhere since the app loaded).
        void refreshOpenSales();
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [status, date, query, page, reloadKey, refreshOpenSales]);

  /** After any change: reload this page, and the tables and open-tab badge. */
  const afterChange = useCallback(() => {
    setReloadKey((k) => k + 1);
    void refreshTables();
  }, [refreshTables]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setModalError(null);
    try {
      await fn();
      setEditing(null);
      setDeleting(null);
      afterChange();
    } catch (err) {
      setModalError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const save = (body: CreateCondimentSaleRequest) => {
    const target = editing;
    if (!target) return;
    void run(() => (target === 'new' ? condimentSalesApi.create(body) : condimentSalesApi.update(target.id, body)));
  };

  const closeModal = () => {
    if (busy) return;
    setEditing(null);
    setDeleting(null);
    setModalError(null);
  };

  const changeStatus = (s: CondimentSaleStatus) => {
    setStatus(s);
    setPage(1);
  };
  const changeDate = (d: string) => {
    setDate(d);
    setPage(1);
  };

  const sales = result?.items ?? [];

  return (
    <>
      <PageHeader
        title="Condiments"
        aside={
          <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setEditing('new')}>
            New tab
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <SegmentedControl legend="Show tabs" hideLegend columns={2} options={STATUS_OPTIONS} value={status} onChange={changeStatus} />
        </div>
        <div className={styles.tools}>
          <TextField
            size="md"
            aria-label="Search tabs by name"
            placeholder="Search tabs"
            prefix={<SearchIcon size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.search}
          />
          <div className={styles.date}>
            <DatePicker label="Day opened" value={date} onChange={changeDate} />
          </div>
        </div>
      </div>

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading tabs…</p>
      ) : sales.length === 0 ? (
        <p className={styles.empty}>{status === 'open' ? 'No open tabs' : 'No billed tabs'}</p>
      ) : (
        <ul className={styles.list} aria-busy={loading}>
          {sales.map((s) => (
            <SaleRow
              key={s.id}
              sale={s}
              onEdit={() => setEditing(s)}
              onDelete={() => setDeleting(s)}
              onBill={canBill ? () => setBilling(s) : undefined}
            />
          ))}
        </ul>
      )}

      {result && <Pagination page={result.page} pageSize={result.pageSize} total={result.total} onPageChange={setPage} />}

      {editing && (
        <SaleFormModal
          key={editing === 'new' ? 'new' : editing.id}
          sale={editing === 'new' ? null : editing}
          catalog={condiments}
          busy={busy}
          error={modalError}
          onSave={save}
          onClose={closeModal}
        />
      )}

      <ConfirmModal
        open={!!deleting}
        title="Delete tab"
        message={<>Are you sure you want to delete <strong>{deleting?.tabName}</strong>'s tab?</>}
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={busy}
        error={modalError}
        onConfirm={() => deleting && void run(() => condimentSalesApi.remove(deleting.id))}
        onCancel={closeModal}
      />

      {billing && (
        <BillStepsModal
          bookings={[]}
          initialSales={[billing]}
          onClose={() => {
            setBilling(null);
            afterChange();
          }}
        />
      )}
    </>
  );
}
